import React, { useState, useEffect } from "react";
import { Typography } from "@mui/material";
import { MessageSquare } from "lucide-react";

// Get responsive phone dimensions based on screen size
const getPhoneDimensions = () => {
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;
  
  // Calculate max available dimensions (optimized to reduce whitespace)
  const maxAvailableHeight = screenHeight - 200; // Reduced from 250 to 200
  
  // Calculate available width based on screen size
  // Account for sidebar, padding, and the form column taking roughly 50% on desktop
  let maxAvailableWidth;
  if (screenWidth < 768) {
    // Mobile: full width minus padding
    maxAvailableWidth = screenWidth - 40;
  } else if (screenWidth < 1024) {
    // Tablet: consider 2-column layout
    maxAvailableWidth = (screenWidth * 0.45) - 40;
  } else {
    // Desktop: consider sidebar + 2-column layout
    maxAvailableWidth = (screenWidth - 250) * 0.45 - 40; // 250px for sidebar
  }
  
  // Define ideal phone dimensions
  const idealWidth = 320;
  const idealHeight = 600;
  const aspectRatio = idealHeight / idealWidth; // ~1.875
  
  // Calculate dimensions that fit within available space while maintaining aspect ratio
  let phoneWidth = Math.min(idealWidth, maxAvailableWidth);
  let phoneHeight = phoneWidth * aspectRatio;
  
  // If height exceeds available space, scale down by height instead
  if (phoneHeight > maxAvailableHeight) {
    phoneHeight = maxAvailableHeight;
    phoneWidth = phoneHeight / aspectRatio;
  }
  
  // Enforce minimum size for usability
  const minWidth = 240;
  if (phoneWidth < minWidth) {
    phoneWidth = minWidth;
    phoneHeight = phoneWidth * aspectRatio;
  }
  
  const scale = phoneWidth / idealWidth;
  
  return {
    width: phoneWidth,
    height: phoneHeight,
    borderWidth: 8 * scale,
    borderRadius: 45 * scale,
  };
};

// Phone preview styles - extracted and adapted for SMS
const getPhoneStyles = () => {
  const dimensions = getPhoneDimensions();
  const scale = dimensions.width / 320; // Calculate scale factor (normalized to desktop width)
  
  return {
    container: {
      position: "relative",
      margin: "0 auto",
      border: `${dimensions.borderWidth}px solid #1A1A1A`,
      borderRadius: `${dimensions.borderRadius}px`,
      height: `${dimensions.height}px`,
      width: `${dimensions.width}px`,
      background: "#000",
      boxShadow:
        "0 0 20px rgba(0, 0, 0, 0.2), inset 0 0 10px rgba(255, 255, 255, 0.1)",
      overflow: "hidden",
    },
    dynamicIsland: {
      position: "absolute",
      top: `${12 * scale}px`,
      left: "50%",
      transform: "translateX(-50%)",
      width: `${120 * scale}px`,
      height: `${25 * scale}px`,
      background: "#000",
      borderRadius: `${20 * scale}px`,
      zIndex: 10,
    },
    screen: {
      height: "100%",
      background: "#fff",
      position: "relative",
      overflow: "hidden",
      borderRadius: `${40 * scale}px`,
      display: "flex",
      flexDirection: "column",
    },
    statusBar: {
      position: "absolute",
      top: `${5 * scale}px`,
      left: 0,
      right: 0,
      padding: `${15 * scale}px`,
      display: "flex",
      justifyContent: "space-between",
      color: "#000",
      fontSize: `${14 * scale}px`,
      zIndex: 5,
    },
    smsHeader: {
      background: "#007AFF", // iOS Messages blue or use #34C759 for green
      color: "#fff",
      padding: `${60 * scale}px ${16 * scale}px ${16 * scale}px`,
      display: "flex",
      alignItems: "center",
      gap: `${12 * scale}px`,
      flexShrink: 0,
    },
    chatBackground: {
      background: "#F2F2F7", // iOS Messages background color
      flex: 1,
      padding: `${16 * scale}px`,
      overflowY: "auto",
      overflowX: "hidden",
    },
    messageBubble: {
      maxWidth: "85%",
      marginLeft: "auto",
      background: "#007AFF", // Blue bubble for sent messages
      borderRadius: `${18 * scale}px`,
      padding: `${12 * scale}px ${16 * scale}px`,
      boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
      marginBottom: `${8 * scale}px`,
      color: "#fff", // White text for sent messages
      fontSize: `${14 * scale}px`,
    },
    receivedMessageBubble: {
      maxWidth: "85%",
      marginRight: "auto",
      marginLeft: "0",
      background: "#E9E9EB", // Gray bubble for received messages
      borderRadius: `${18 * scale}px`,
      padding: `${12 * scale}px ${16 * scale}px`,
      boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
      marginBottom: `${8 * scale}px`,
      color: "#000", // Black text for received messages
      fontSize: `${14 * scale}px`,
    },
    messageTime: {
      fontSize: `${11 * scale}px`,
      opacity: 0.7,
      marginTop: `${4 * scale}px`,
      textAlign: "right",
    },
    inputArea: {
      position: "absolute",
      bottom: "0",
      left: "0",
      right: "0",
      background: "#F2F2F7",
      padding: `${12 * scale}px ${16 * scale}px`,
      borderTop: "1px solid #C6C6C8",
      display: "flex",
      alignItems: "center",
      gap: `${8 * scale}px`,
    },
    textInput: {
      flex: 1,
      background: "#fff",
      border: "1px solid #C6C6C8",
      borderRadius: `${20 * scale}px`,
      padding: `${8 * scale}px ${16 * scale}px`,
      outline: "none",
      fontSize: `${16 * scale}px`,
    },
    sendButton: {
      background: "#007AFF",
      color: "#fff",
      border: "none",
      borderRadius: "50%",
      width: `${36 * scale}px`,
      height: `${36 * scale}px`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      fontSize: `${18 * scale}px`,
    },
  };
};

export default function SMSPreview({ messageText, recipientName, showInput = true }) {
  const [phoneStyles, setPhoneStyles] = useState(getPhoneStyles());

  useEffect(() => {
    const handleResize = () => {
      setPhoneStyles(getPhoneStyles());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const dimensions = getPhoneDimensions();
  const scale = dimensions.width / 320; // Normalized to desktop width
  const iconSize = Math.max(16, 20 * scale);

  return (
    <div style={phoneStyles.container}>
      {/* Dynamic Island (iPhone 14+ style) */}
      <div style={phoneStyles.dynamicIsland}></div>
      {/* Phone Screen */}
      <div style={phoneStyles.screen}>
        {/* Status Bar */}
        <div style={phoneStyles.statusBar}>
          <span>9:41</span>
          <span>100%</span>
        </div>
        {/* SMS Header */}
        <div style={phoneStyles.smsHeader}>
          <MessageSquare size={iconSize} />
          <div>
            <Typography variant="subtitle2" sx={{ fontSize: `${14 * scale}px` }}>
              {recipientName || "Messages"}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7, fontSize: `${12 * scale}px` }}>
              SMS Preview
            </Typography>
          </div>
        </div>
        {/* Chat Background */}
        <div style={phoneStyles.chatBackground}>
          {/* Sample received message */}
          <div style={phoneStyles.receivedMessageBubble}>
            <Typography variant="body2" sx={{ fontSize: `${14 * scale}px` }}>
              Hey! How are you doing?
            </Typography>
            <div style={{ ...phoneStyles.messageTime, color: "#8E8E93" }}>
              2:30 PM
            </div>
          </div>
          {/* Your SMS message (sent) */}
          <div style={phoneStyles.messageBubble}>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", fontSize: `${14 * scale}px` }}>
              {messageText || "Your SMS message will appear here"}
            </Typography>
            <div style={phoneStyles.messageTime}>
              2:32 PM
            </div>
          </div>
        </div>
        {/* Input Area (optional) */}
        {showInput && (
          <div style={phoneStyles.inputArea}>
            <input 
              type="text" 
              placeholder="Text Message" 
              style={phoneStyles.textInput}
            />
            <button style={phoneStyles.sendButton}>
              ↑
            </button>
          </div>
        )}
      </div>
    </div>
  );
} 